import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Heart, Users, Calendar, CreditCard, Star } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
import { useLanguage } from '../context/LanguageContext';
export function MembershipPage() {
  const {
    t,
    isRTL
  } = useLanguage();
  const yearlyBenefits = t('membership.yearlyBenefits') as unknown as string[];
  const lifetimeBenefits = t('membership.lifetimeBenefits') as unknown as string[];
  return <>
      <SEO title={t('nav.membership')} description={t('membership.hero.subtitle')} data-id="element-485" />

      <div className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white py-20 md:py-24" data-id="element-486">
        <div className="container mx-auto px-4 text-center" data-id="element-487">
          <FadeIn data-id="element-488">
            <Users className="h-12 w-12 mx-auto mb-6 text-primary-200" data-id="element-489" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-490">
              {t('membership.hero.title')}
            </h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto leading-relaxed" data-id="element-491">
              {t('membership.hero.subtitle')}
            </p>
          </FadeIn>
        </div>
      </div>

      <Section data-id="element-492">
        <div className="max-w-6xl mx-auto" data-id="element-493">
          {/* Membership Tiers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12" data-id="element-494">
            {/* Yearly Membership */}
            <FadeIn delay={0} data-id="element-495">
              <Card className="border-2 border-primary-200 shadow-xl relative overflow-hidden h-full flex flex-col" data-id="element-496">
                <CardHeader className="text-center pb-6 pt-8" data-id="element-497">
                  <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-4 mx-auto" data-id="element-498">
                    <Calendar className="h-8 w-8" data-id="element-499" />
                  </div>
                  <CardTitle className="text-3xl mb-2" data-id="element-500">
                    {t('membership.yearly.title')}
                  </CardTitle>
                  <CardDescription className="text-base" data-id="element-501">
                    {t('membership.yearly.subtitle')}
                  </CardDescription>
                  <div className="mt-6" data-id="element-502">
                    <div className="text-5xl font-bold text-gray-900" data-id="element-503">
                      {t('membership.yearly.price')}
                    </div>
                    <div className="text-gray-500 mt-1" data-id="element-504">
                      {t('membership.yearly.period')}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col" data-id="element-505">
                  <div className="flex-1" data-id="element-506">
                    <h4 className="font-bold text-gray-900 mb-4" data-id="element-507">
                      {t('membership.benefits')}
                    </h4>
                    <ul className="space-y-3 mb-8" data-id="element-508">
                      {yearlyBenefits.map((benefit, i) => <li key={i} className="flex items-start text-sm" data-id="element-509">
                          <Check className={`h-5 w-5 text-green-600 shrink-0 mt-0.5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-510" />
                          <span className="text-gray-700" data-id="element-511">{benefit}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="pt-6 border-t" data-id="element-512">
                    <a href="https://buy.stripe.com/test_yearly_placeholder" target="_blank" rel="noopener noreferrer" data-id="element-513">
                      <Button variant="secondary" className="w-full" size="lg" data-id="element-514">
                        <CreditCard className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-515" />
                        {t('membership.yearly.button')}
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </FadeIn>

            {/* Lifetime Membership */}
            <FadeIn delay={0.1} data-id="element-516">
              <Card className="border-2 border-accent-200 shadow-xl h-full flex flex-col relative" data-id="element-517">
                <div className="absolute top-0 right-0 bg-accent-600 text-white px-4 py-1 text-sm font-bold" data-id="element-518">
                  {t('membership.lifetime.badge')}
                </div>
                <CardHeader className="text-center pb-6 pt-8" data-id="element-519">
                  <div className="h-16 w-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-4 mx-auto" data-id="element-520">
                    <Star className="h-8 w-8" data-id="element-521" />
                  </div>
                  <CardTitle className="text-3xl mb-2" data-id="element-522">
                    {t('membership.lifetime.title')}
                  </CardTitle>
                  <CardDescription className="text-base" data-id="element-523">
                    {t('membership.lifetime.subtitle')}
                  </CardDescription>
                  <div className="mt-6" data-id="element-524">
                    <div className="text-5xl font-bold text-gray-900" data-id="element-525">
                      {t('membership.lifetime.price')}
                    </div>
                    <div className="text-gray-500 mt-1" data-id="element-526">
                      {t('membership.lifetime.period')}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col" data-id="element-527">
                  <div className="flex-1" data-id="element-528">
                    <h4 className="font-bold text-gray-900 mb-4" data-id="element-529">
                      {t('membership.benefits')}
                    </h4>
                    <ul className="space-y-3 mb-8" data-id="element-530">
                      {lifetimeBenefits.map((benefit, i) => <li key={i} className="flex items-start text-sm" data-id="element-531">
                          <Check className={`h-5 w-5 text-green-600 shrink-0 mt-0.5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-532" />
                          <span className="text-gray-700" data-id="element-533">{benefit}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="pt-6 border-t" data-id="element-534">
                    <a href="https://buy.stripe.com/test_lifetime_placeholder" target="_blank" rel="noopener noreferrer" data-id="element-535">
                      <Button variant="secondary" className="w-full" size="lg" data-id="element-536">
                        <CreditCard className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-537" />
                        {t('membership.lifetime.button')}
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </FadeIn>
          </div>

          <div className="text-center text-gray-500 text-sm italic mb-16" data-id="element-538">
            {t('membership.nonRefundable')}
          </div>
        </div>
      </Section>

      {/* Why Join Section */}
      <Section background="gray" data-id="element-539">
        <div className="max-w-4xl mx-auto" data-id="element-540">
          <FadeIn className="text-center mb-12" data-id="element-541">
            <h2 className="text-3xl font-serif font-bold text-primary-900 mb-4" data-id="element-542">
              {t('membership.whyJoin.title')}
            </h2>
            <p className="text-gray-600 text-lg" data-id="element-543">
              {t('membership.whyJoin.subtitle')}
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-544">
            {[{
            icon: Users,
            title: t('membership.whyJoin.network.title'),
            desc: t('membership.whyJoin.network.description')
          }, {
            icon: Calendar,
            title: t('membership.whyJoin.events.title'),
            desc: t('membership.whyJoin.events.description')
          }, {
            icon: Heart,
            title: t('membership.whyJoin.impact.title'),
            desc: t('membership.whyJoin.impact.description')
          }].map((item, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-545">
                <Card className="h-full border-none shadow-md" data-id="element-546">
                  <CardContent className="p-6 flex flex-col items-center text-center" data-id="element-547">
                    <div className="h-12 w-12 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center shrink-0 mb-4" data-id="element-548">
                      <item.icon className="h-6 w-6" data-id="element-549" />
                    </div>
                    <div data-id="element-550">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-551">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-552">{item.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* FAQ / Contact Section */}
      <Section className="text-center" data-id="element-553">
        <FadeIn data-id="element-554">
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4" data-id="element-555">
            {t('membership.questions.title')}
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-556">
            {t('membership.questions.subtitle')}
          </p>
          <Link to="/contact" data-id="element-557">
            <Button variant="outline" size="lg" data-id="element-558">
              {t('membership.questions.button')}
            </Button>
          </Link>
        </FadeIn>
      </Section>
    </>;
}